import {defaults, isEmpty} from 'lodash';
import React, {useState, useRef, useEffect, useMemo} from 'react';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {
    findProps,
    inRightPath,
    getFirstToMoveColorFromRoot,
    isRightNode,
    findProp,
    calcScoreDiffText,
    Analysis,
    a1ToPos,
    SGF_LETTERS,
    calcAnalysisPointColor,
    getRoot,
    Sgf,
    isForceNode,
    isPreventMoveNode,
    isTargetNode,
    calcMatAndMarkup,
    addMoveToCurrentNode,
    Ki,
    TNode,
} from 'ghostban';
import {useHotkeys} from 'react-hotkeys-hook';
import {Base64} from 'js-base64';
import {useTranslation} from 'react-i18next';

import {
    findNodeFromCompressedPath,
    getCompressedPathFromNode,
    getFullPath,
    inUserCompressedPath,
    isLatestUserCommentNode,
    isSgfCommentNode,
    isUserCompressedPathNode,
    useDeepCompareEffect,
    useDispatch,
    useSgfTree,
    isReachable,
    checkPlayerCanLeadToRight,
    checkComputerWillLeadToWrong,
    getNodeA1Path,
    getSgfNodeIds,
} from '@utils';
import {
    svgEquilateralTriangle,
    svgCircle,
    svgEl,
    svgLabel,
    svgLine,
    svgStone,
    svgSquare,
    svgDoubleLine,
    svgIcon,
    svgRing,
} from '@utils/svg';
import {boardSlice} from '@reducers';
import {ChangedCompressedPath, ChangedNode, SgfDiffType} from '@types';
import {icon} from '@fortawesome/fontawesome-svg-core';
import {
    faHouse,
    faCircleInfo,
    faBullseye,
    faCirclePlus,
    faCircleMinus,
} from '@fortawesome/free-solid-svg-icons';
import {faComment} from '@fortawesome/free-regular-svg-icons';
import {useTheme} from '@components/theme/useTheme';

export const COLOR_DARK_GREEN = '#65a30d'; // tailwind lime-600
export const COLOR_LIGHT_GREEN = '#a3e635'; // tailwind lime-400
export const COLOR_RED = '#DC2626'; // tailwind red-600
export const COLOR_LIGHT_RED = '#ef4444'; // tailwind red-600
export const COLOR_BLUE = '#06b6d4';
export const COLOR_SKY_300 = '#7DD3FC'; // tailwind sky-300;
export const COLOR_GRAY = '#737373'; // tailwind neutral-500
export const GP_SVG_STONE_POINTER_CLASSNAME = 'gp-svg-stone-pointer';
export const DOTTED_LINE_DASH_ARRAY = '30, 14';
export const LINE_ADORNMENT_SIZE = 40;
const CURRENT_NODE_SELECTOR = '#sgf-tree-current-node';

const styleDeletedPreviewLine = (line: SVGElement, width: number) => {
    line.style.opacity = '0.5';
    [line, ...Array.from(line.querySelectorAll('line'))].forEach(element => {
        element.setAttribute('stroke', COLOR_RED);
        element.setAttribute('stroke-dasharray', DOTTED_LINE_DASH_ARRAY);
        element.setAttribute('stroke-width', width.toString());
    });
};

const SCALE = 0.2;

const enum ArrowBehavior {
    Branch,
    Node,
}

const defaultOptions = {
    size: 110,
    scale: 0.2,
    minWidth: 110 * 3 * 0.25,
    minHeight: 110 * 3 * 0.25,
    maxWidth: 800,
    maxHeight: 600,
    gap: [60, 40],
    // tailwind red-600 default wrong line color
    defaultLineColor: '#DC2626',
    defaultLineWidth: 6,
    defaultLineDashArray: '',
    activeLineColor: '',
    activeLineWidth: 16,
    inactiveLineColor: '#737373',
    // tailwind lime-600
    rightLineColor: COLOR_DARK_GREEN,
    rightNodeColor: COLOR_DARK_GREEN,
    // tailwind lime-400
    lightRightLineColor: COLOR_LIGHT_GREEN,
    lightRightNodeColor: COLOR_LIGHT_GREEN,
    // tailwind red-600
    wrongLineColor: COLOR_RED,
    offPathLineDashArray: DOTTED_LINE_DASH_ARRAY,
    forceAdornmentSize: LINE_ADORNMENT_SIZE,
    preventMoveAdornmentSize: LINE_ADORNMENT_SIZE,
    currentNodeHighlightColor: COLOR_SKY_300,
    squareHighlightExtent: 4,
    circleHighlightExtent: 2,
    doubleDottedLineGap: 24,
    arrowBehavior: ArrowBehavior.Branch,
};

const allPositions: {
    [key: string]: {
        x: number;
        y: number;
        treeDepth: number;
        treeHeight: number;
        path: TNode[];
        offPath: boolean;
        isReachable: boolean;
        computerWillLeadToWrong: boolean;
        playerCanLeadToRight: boolean;
        showInPathOnly?: boolean;
        inCurrentPath?: boolean;
    };
} = {};

const pathOnlyPositions: {
    [key: string]: {
        x: number;
        y: number;
        treeDepth: number;
        treeHeight: number;
        path: TNode[];
        offPath: boolean;
        isReachable: boolean;
        computerWillLeadToWrong: boolean;
        playerCanLeadToRight: boolean;
        showInPathOnly?: boolean;
        inCurrentPath?: boolean;
    };
} = {};

const inNodePath = (
    node: TNode,
    targetNode: TNode,
    activeBranchIndexes: {[key: string]: number} = {}
) => {
    const path = getFullPath(targetNode, activeBranchIndexes);
    return path.filter(n => n.model.id === node.model.id).length > 0;
};

export const SgfTreePanel = ({
    baseSgf,
    baselineSgf,
    editPathMarkersSgf,
    commentCompressedPaths = [],
    rightCompressedPaths = [],
    wrongCompressedPaths = [],
    questionCompressedPaths = [],
    changedCompressedPaths = [],
    showEditPathMarkers = false,
    onCurrentNodeChange,
    onDeletedNodeSelect,
    onNodeRightClick,
    options = defaultOptions,
    showAnalysis = false,
    showTotalLoss = false,
    pathCounts,
    showDeletedPaths = false,
    showDeletedEditNodes = false,
    showOffPath = true,
    showCurrentPathOnly = false,
    baseRootNode: externalBaseRootNode,
    disabled,
    autoScrollToCurrentNode,
    defautlActiveBranchIndexes = {},
    lazyLoadStates,
    onLoadLazyNode,
}: {
    baseSgf?: string;
    baselineSgf?: string;
    editPathMarkersSgf?: string;
    commentCompressedPaths?: string[];
    rightCompressedPaths?: string[];
    wrongCompressedPaths?: string[];
    questionCompressedPaths?: string[];
    changedCompressedPaths?: ChangedCompressedPath[];
    showEditPathMarkers?: boolean;
    changedNodes?: ChangedNode[];
    onCurrentNodeChange?: (node: TNode) => void;
    onDeletedNodeSelect?: (node: TNode | null) => void;
    onNodeRightClick?: (node: TNode, event: MouseEvent) => void;
    options?: any;
    showAnalysis?: boolean;
    showTotalLoss?: boolean;
    pathCounts?: Record<string, number>;
    showDeletedPaths?: boolean;
    showDeletedEditNodes?: boolean;
    showOffPath?: boolean;
    showCurrentPathOnly?: boolean;
    baseRootNode?: TNode | null;
    disabled?: boolean;
    autoScrollToCurrentNode?: boolean;
    defautlActiveBranchIndexes?: {[key: string]: number};
    lazyLoadStates?: Record<
        string,
        {childrenCount: number; childrenLoaded: boolean; isLoading: boolean}
    >;
    onLoadLazyNode?: (node: TNode) => void;
}) => {
    const {t} = useTranslation();
    const theme = useTheme();
    const [width, setWidth] = useState(
        options.minWidth ?? defaultOptions.minWidth
    );
    const [height, setHeight] = useState(
        options.minHeight ?? defaultOptions.minHeight
    );
    const [baseRootNode, setBaseRootNode] = useState<TNode | null>();
    const [activeBranchIndexes, setActiveBranchIndexes] = useState<{
        [key: string]: number;
    }>(defautlActiveBranchIndexes);

    // Use external baseRootNode if provided, otherwise use internal one
    const activeRootNode = externalBaseRootNode ?? baseRootNode;
    const baselineNodeIds = useMemo(
        () => getSgfNodeIds(baselineSgf),
        [baselineSgf]
    );
    const svgRef = useRef<SVGSVGElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const {
        currentNode: originalCurrentNode,
        update: originalUpdate,
        root: originalRoot,
    } = useSgfTree({allowCustomProps: true});
    const [currentNode, setCurrentNode] = useState<TNode | undefined>(
        originalCurrentNode
    );
    const [root, setRoot] = useState<TNode | undefined>(originalRoot);
    const [update, setUpdate] = useState<number>(originalUpdate);
    const editedNodeIds = useMemo(() => {
        if (!showEditPathMarkers) return null;
        if (editPathMarkersSgf) return getSgfNodeIds(editPathMarkersSgf);
        if (!originalRoot) return null;

        const ids = new Set<string>();
        originalRoot.walk((node: TNode) => {
            ids.add(node.model.id);
            return true;
        });
        return ids;
        // The SGF root is mutated in place while editing; its update counter invalidates this memo.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showEditPathMarkers, editPathMarkersSgf, originalRoot, originalUpdate]);
    const deletedPreviewNodeIds = useMemo(() => {
        if (!showDeletedEditNodes || !baselineNodeIds || !editedNodeIds) {
            return new Set<string>();
        }
        return new Set(
            [...baselineNodeIds].filter(id => !editedNodeIds.has(id))
        );
    }, [showDeletedEditNodes, baselineNodeIds, editedNodeIds]);
    const deletedEditPreviewRoot = useMemo(() => {
        if (
            !showDeletedEditNodes ||
            !originalRoot ||
            !baselineSgf ||
            !editedNodeIds
        ) {
            return null;
        }

        const previewRoot = new Sgf(new Sgf(originalRoot).toSgf(), {
            allowCustomProps: true,
        }).root;
        const baselineRoot = new Sgf(baselineSgf).root;
        if (!previewRoot || !baselineRoot) return null;

        const deletedBranchRoots = baselineRoot.all(
            (node: TNode) =>
                !editedNodeIds.has(node.model.id) &&
                !!node.parent &&
                editedNodeIds.has(node.parent.model.id)
        );
        deletedBranchRoots.forEach(node => {
            const parentId = node.parent?.model.id;
            if (parentId) {
                const previewParent = previewRoot.first(
                    n => n.model.id === parentId
                );
                if (previewParent) previewParent.addChild(node.drop());
            }
        });
        return previewRoot;
    }, [showDeletedEditNodes, originalRoot, baselineSgf, editedNodeIds]);

    const dispatch = useDispatch();
    const optionsWithDefault = defaults({}, options, defaultOptions);
    const {
        size,
        minWidth: minWidthOption,
        maxWidth,
        minHeight: minHeightOption,
        maxHeight,
        gap,
        defaultLineColor,
        defaultLineWidth,
        defaultLineDashArray,
        inactiveLineColor,
        activeLineWidth,
        rightLineColor,
        wrongLineColor,
        offPathLineDashArray,
        forceAdornmentSize,
        preventMoveAdornmentSize,
        doubleDottedLineGap,
        currentNodeHighlightColor,
        squareHighlightExtent,
        circleHighlightExtent,
        arrowBehavior,
    } = optionsWithDefault;
    const stoneSize = (size / 2) * 0.8;
    const minHeight = minHeightOption / SCALE;
    const minWidth = minWidthOption / SCALE;

    let treeDepth = 0;
    let treeHeight = 0;
    let treeLeafCount = 0;
    let pos = {top: 0, left: 0, x: 0, y: 0};

    const scrollToCurrentNode = () => {
        const container = containerRef.current;
        if (!svgRef.current || !container) return;

        const currentNodeElement =
            container.querySelector<SVGGraphicsElement>(CURRENT_NODE_SELECTOR);
        if (!currentNodeElement) return;

        const containerRect = container.getBoundingClientRect();
        const currentNodeRect = currentNodeElement.getBoundingClientRect();
        let left = container.scrollLeft;
        let top = container.scrollTop;

        if (currentNodeRect.left < containerRect.left) {
            left += currentNodeRect.left - containerRect.left;
        } else if (currentNodeRect.right > containerRect.right) {
            left += currentNodeRect.right - containerRect.right;
        }

        if (currentNodeRect.top < containerRect.top) {
            top += currentNodeRect.top - containerRect.top;
        } else if (currentNodeRect.bottom > containerRect.bottom) {
            top += currentNodeRect.bottom - containerRect.bottom;
        }

        if (left === container.scrollLeft && top === container.scrollTop) {
            return;
        }

        container.scrollTo({left, top, behavior: 'smooth'});
    };

    const handleNodeChange = (node: TNode) => {
        if (disabled) return;
        if (showDeletedEditNodes && deletedPreviewNodeIds.has(node.model.id)) {
            setCurrentNode(node);
            onDeletedNodeSelect?.(node);
            return;
        }

        const editableNode = showDeletedEditNodes
            ? originalRoot?.first(n => n.model.id === node.model.id)
            : node;
        if (editableNode) {
            setCurrentNode(node);
            onDeletedNodeSelect?.(null);
            if (onCurrentNodeChange) {
                onCurrentNodeChange(editableNode);
            } else {
                dispatch(boardSlice.actions.setCurrentNode(editableNode));
            }
            if (autoScrollToCurrentNode) scrollToCurrentNode();
        }
    };

    const getNeighborNode = (currentNode: TNode, direction: 'up' | 'down') => {
        if (currentNode && currentNode.parent && root) {
            const depth = currentNode.getPath().length;
            const sameDepthNodes = root.all(
                (node: TNode) => node.getPath().length === depth
            );
            const currentIndex = sameDepthNodes.findIndex(
                (node: TNode) => node.model.id === currentNode.model.id
            );

            let neighborNode: TNode | undefined;
            if (direction === 'up') {
                neighborNode = sameDepthNodes[currentIndex - 1];
            } else if (direction === 'down') {
                neighborNode = sameDepthNodes[currentIndex + 1];
            }
            if (neighborNode) return neighborNode;
        }
        return null;
    };

    const calcRightColor = (node: TNode) => {
        const {isReachable, computerWillLeadToWrong, playerCanLeadToRight} =
            allPositions[node.model.id];

        let color = defaultLineColor;

        if (playerCanLeadToRight) {
            if (node.isRoot() && node.children.length > 0) {
                const rightPathChildren = node.children.filter(child =>
                    inRightPath(child)
                );
                if (rightPathChildren.length > 0) {
                    const hasReachableChild = rightPathChildren.some(child => {
                        const childPosition = allPositions[child.model.id];
                        return childPosition?.isReachable;
                    });
                    return hasReachableChild
                        ? COLOR_DARK_GREEN
                        : COLOR_LIGHT_GREEN;
                }
            }

            if (node.parent) {
                color = isReachable ? COLOR_DARK_GREEN : COLOR_LIGHT_GREEN;
            }
        }

        if (computerWillLeadToWrong && !isReachable) {
            color = defaultLineColor;
        }

        return color;
    };

    const getCustomPropValue = (node: TNode, token: string) => {
        const prop = node.model?.customProps?.find(
            (p: {token: string}) => p.token === token
        );
        return prop?.value;
    };

    const isResponseInvalid = (node: TNode) => {
        return getCustomPropValue(node, 'RV') === 'false';
    };

    const isAnalysisIncomplete = (node: TNode) => {
        return getCustomPropValue(node, 'IA') === 'false';
    };

    const getAnalysisLineStyle = (
        node: TNode | undefined,
        color: string,
        dashArray: string
    ) => {
        if (!node) {
            return {color, dashArray, invalid: false};
        }

        const invalid = isResponseInvalid(node);
        return {
            color: invalid ? COLOR_GRAY : color,
            dashArray: isAnalysisIncomplete(node)
                ? DOTTED_LINE_DASH_ARRAY
                : dashArray,
            invalid,
        };
    };

    const applyAnalysisLineStyle = (
        line: SVGElement,
        style: {invalid: boolean}
    ) => {
        if (style.invalid) {
            line.setAttribute('stroke', COLOR_GRAY);
            line.querySelectorAll('line').forEach(child => {
                child.setAttribute('stroke', COLOR_GRAY);
            });
        }
    };

    useHotkeys(
        'ArrowRight',
        () => {
            if (!currentNode) return;
            if (
                arrowBehavior === ArrowBehavior.Node &&
                currentNode.children[0]
            ) {
                handleNodeChange(currentNode.children[0]);
            }

            if (arrowBehavior === ArrowBehavior.Branch) {
                const node = currentNode.children.find((n: TNode) =>
                    inNodePath(n, currentNode, activeBranchIndexes)
                );
                if (node) {
                    handleNodeChange(node);
                }
            }
        },
        {preventDefault: true},
        [currentNode, activeBranchIndexes]
    );

    useHotkeys(
        'ArrowLeft',
        () => {
            if (!currentNode) return;
            if (currentNode && currentNode.parent) {
                handleNodeChange(currentNode.parent);
            }
        },
        {preventDefault: true},
        [currentNode, activeBranchIndexes]
    );

    useHotkeys(
        'ArrowUp',
        () => {
            if (!currentNode) return;
            if (arrowBehavior === ArrowBehavior.Node) {
                const node = getNeighborNode(currentNode, 'up');
                if (node) {
                    handleNodeChange(node);
                }
            }
            if (arrowBehavior === ArrowBehavior.Branch) {
                if (activeBranchIndexes[currentNode.model.id] > 0) {
                    setActiveBranchIndexes((prev: {[key: string]: number}) => ({
                        ...prev,
                        [currentNode.model.id]: prev[currentNode.model.id] - 1,
                    }));
                }
            }
        },
        {preventDefault: true},
        [currentNode, activeBranchIndexes]
    );

    useHotkeys(
        'ArrowDown',
        () => {
            if (!currentNode) return;
            if (arrowBehavior === ArrowBehavior.Node) {
                const node = getNeighborNode(currentNode, 'down');
                if (node) {
                    handleNodeChange(node);
                }
            }
            if (arrowBehavior === ArrowBehavior.Branch) {
                const {children} = currentNode;
                const maxIndex = children.length - 1;
                const activeIndex =
                    activeBranchIndexes[currentNode.model.id] || 0;
                if (activeIndex < maxIndex) {
                    setActiveBranchIndexes((prev: {[key: string]: number}) => ({
                        ...prev,
                        [currentNode.model.id]:
                            (prev[currentNode.model.id] || 0) + 1,
                    }));
                } else {
                    setActiveBranchIndexes((prev: {[key: string]: number}) => ({
                        ...prev,
                        [currentNode.model.id]: maxIndex,
                    }));
                }
            }
        },
        {preventDefault: true},
        [currentNode, activeBranchIndexes]
    );

    useHotkeys(
        'home',
        () => {
            if (currentNode) {
                const node = getRoot(currentNode);
                if (node) {
                    handleNodeChange(node);
                }
            }
        },
        {preventDefault: true},
        [currentNode]
    );

    useHotkeys(
        'end',
        () => {
            let node = currentNode;
            while (node) {
                if (node.children.length > 0) {
                    node = node.children[0];
                } else {
                    break;
                }
            }
            if (node) {
                handleNodeChange(node);
            }
        },
        {preventDefault: true},
        [currentNode]
    );

    const calPos = (length: number, index: number, node: TNode) => {
        let x = size * length + size / 2;
        const y = size * index + size / 2 + gap[1] * index;
        if (node && node.parent) {
            x = x + gap[0] * length;
        }

        return {x, y};
    };

    // Calculate positions for all nodes but only in the current path
    const calcPathOnlyPositions = (root: TNode) => {
        let treeHeight = 0;
        const currentPathNodeIds = new Set(
            currentNode?.getPath().map(node => node.model.id) ?? []
        );

        root.walk((node: TNode) => {
            const isOnCurrentPath = currentPathNodeIds.has(node.model.id);
            const path = node.getPath();
            const isDescendantOfCurrent =
                currentNode &&
                path.some(n => n.model.id === currentNode.model.id) &&
                !isOnCurrentPath;

            const length = path.length - 1;
            treeDepth = Math.max(length, treeDepth) ?? 0;
            if (isDescendantOfCurrent) {
                treeHeight += node.getIndex() > 0 ? 1 : 0;
            }

            if (showCurrentPathOnly) {
                treeLeafCount = treeHeight;
            }

            // const {x, y} = calPos(length, treeHeight, node);
            const {x, y} = calPos(length, treeHeight, node);

            pathOnlyPositions[node.model.id] = {
                x,
                y,
                treeDepth,
                treeHeight,
                path,
                offPath:
                    showOffPath && activeRootNode
                        ? !activeRootNode.first(
                              (n: TNode) => n.model.id === node.model.id
                          )
                        : false,
                isReachable: isReachable(node),
                computerWillLeadToWrong: false,
                playerCanLeadToRight: false,
                showInPathOnly: isDescendantOfCurrent || isOnCurrentPath,
                inCurrentPath: isOnCurrentPath,
            };
            return true;
        });
    };

    const calcPositions = (root: TNode) => {
        root.walk((node: TNode) => {
            const path = node.getPath();
            const length = path.length - 1;
            treeDepth = Math.max(length, treeDepth) ?? 0;
            treeHeight += node.getIndex() > 0 ? 1 : 0;
            if (!showCurrentPathOnly) {
                treeLeafCount = treeHeight;
            }
            const {x, y} = calPos(length, treeHeight, node);
            allPositions[node.model.id] = {
                x,
                y,
                treeDepth,
                treeHeight,
                path,
                offPath:
                    showOffPath && activeRootNode
                        ? !activeRootNode.first(
                              (n: TNode) => n.model.id === node.model.id
                          )
                        : false,
                isReachable: isReachable(node),
                computerWillLeadToWrong: false,
                playerCanLeadToRight: false,
            };
            return true;
        });

        // Build a set of offPath node IDs so that checkComputerWillLeadToWrong
        // does not traverse into offPath subtrees
        const offPathNodeIds = new Set<string>();
        if (showOffPath && activeRootNode) {
            for (const id of Object.keys(allPositions)) {
                if (allPositions[id].offPath) {
                    offPathNodeIds.add(id);
                }
            }
        }
        const excludeIds = offPathNodeIds.size > 0 ? offPathNodeIds : undefined;

        root.walk((node: TNode) => {
            const position = allPositions[node.model.id];
            if (position) {
                // Check if any node in the path has computerWillLeadToWrong
                const hasComputerWillLeadToWrongInPath = position.path.some(
                    (pathNode: TNode) => {
                        const pathPosition = allPositions[pathNode.model.id];
                        return pathPosition?.computerWillLeadToWrong;
                    }
                );
                position.computerWillLeadToWrong =
                    hasComputerWillLeadToWrongInPath ||
                    checkComputerWillLeadToWrong(node, excludeIds);
                position.playerCanLeadToRight = checkPlayerCanLeadToRight(node);
            }
            return true;
        });
    };

    const drawLines = () => {
        if (!root || !currentNode) return;

        const positions = showCurrentPathOnly
            ? pathOnlyPositions
            : allPositions;

        root.walk((node: TNode) => {
            if (svgRef.current && positions[node.model.id]) {
                if (
                    showCurrentPathOnly &&
                    !positions[node.model.id].showInPathOnly
                ) {
                    return true;
                }

                const {x, y, offPath} = positions[node.model.id];
                const firstChildNode = node.children[0];

                const lineColor = calcRightColor(node);
                let lineWidth = defaultLineWidth;
                let lineDashArray = defaultLineDashArray;

                if (inNodePath(node, currentNode, activeBranchIndexes)) {
                    lineWidth = activeLineWidth;
                }

                if (offPath) {
                    lineDashArray = offPathLineDashArray;
                }

                const nodeCompressedPath = getCompressedPathFromNode(node);
                const newPathMatches = changedCompressedPaths.find(n => {
                    return (
                        n.fullPath.startsWith(nodeCompressedPath) &&
                        !n.basePath.startsWith(nodeCompressedPath) &&
                        !isEmpty(nodeCompressedPath)
                    );
                });
                const firstChildIsAdded =
                    firstChildNode &&
                    baselineNodeIds?.has(firstChildNode.model.id) === false;
                const firstChildIsOffPath = Boolean(
                    firstChildNode && positions[firstChildNode.model.id]?.offPath
                );
                const nodeIsAdded = baselineNodeIds?.has(node.model.id) === false;

                // Draw horizontal line
                const linesEl = svgEl('g');
                let horizontalLineColor = lineColor;
                let horizontalLineWidth = lineWidth;
                let horizontalLineDashArray = lineDashArray;
                // If current node in right path but its first child node not in the right path. should be in red color
                if (
                    firstChildNode &&
                    inRightPath(node) &&
                    !inRightPath(firstChildNode)
                ) {
                    horizontalLineColor = wrongLineColor;
                }

                // if (positions[node.model.id]?.hasRefutation) {
                //     horizontalLineColor = wrongLineColor;
                // }

                // Same logic as above but for the active path
                if (
                    firstChildNode &&
                    !inNodePath(
                        firstChildNode,
                        currentNode,
                        activeBranchIndexes
                    )
                ) {
                    horizontalLineWidth = defaultLineWidth;
                }

                // For off path nodes
                if (firstChildIsOffPath) {
                    horizontalLineDashArray = offPathLineDashArray;
                }

                if (
                    firstChildIsAdded ||
                    newPathMatches?.changeType === SgfDiffType.Delete
                ) {
                    horizontalLineDashArray = DOTTED_LINE_DASH_ARRAY;
                }

                if (node.hasChildren()) {
                    const horizontalAnalysisStyle = getAnalysisLineStyle(
                        firstChildNode,
                        horizontalLineColor,
                        horizontalLineDashArray
                    );
                    horizontalLineColor = horizontalAnalysisStyle.color;
                    horizontalLineDashArray = firstChildIsAdded
                        ? DOTTED_LINE_DASH_ARRAY
                        : horizontalAnalysisStyle.dashArray;

                    let horizontalLine;
                    if (
                        inUserCompressedPath(node, commentCompressedPaths) &&
                        firstChildNode &&
                        inUserCompressedPath(
                            firstChildNode,
                            commentCompressedPaths
                        ) &&
                        firstChildIsOffPath
                    ) {
                        horizontalLine = svgDoubleLine(
                            x,
                            y,
                            x + stoneSize + gap[0] + (size - stoneSize),
                            y,
                            inactiveLineColor,
                            horizontalLineWidth,
                            doubleDottedLineGap,
                            horizontalLineDashArray
                        );
                    } else if (
                        inUserCompressedPath(node, rightCompressedPaths) &&
                        firstChildNode &&
                        inUserCompressedPath(
                            firstChildNode,
                            rightCompressedPaths
                        ) &&
                        firstChildIsOffPath
                    ) {
                        horizontalLine = svgDoubleLine(
                            x,
                            y,
                            x + stoneSize + gap[0] + (size - stoneSize),
                            y,
                            COLOR_DARK_GREEN,
                            horizontalLineWidth,
                            doubleDottedLineGap,
                            horizontalLineDashArray
                        );
                    } else if (
                        inUserCompressedPath(node, wrongCompressedPaths) &&
                        firstChildNode &&
                        inUserCompressedPath(
                            firstChildNode,
                            wrongCompressedPaths
                        ) &&
                        firstChildIsOffPath
                    ) {
                        horizontalLine = svgDoubleLine(
                            x,
                            y,
                            x + stoneSize + gap[0] + (size - stoneSize),
                            y,
                            COLOR_RED,
                            horizontalLineWidth,
                            doubleDottedLineGap,
                            horizontalLineDashArray
                        );
                    } else if (
                        inUserCompressedPath(node, questionCompressedPaths) &&
                        firstChildNode &&
                        inUserCompressedPath(
                            firstChildNode,
                            questionCompressedPaths
                        ) &&
                        firstChildIsOffPath
                    ) {
                        horizontalLine = svgDoubleLine(
                            x,
                            y,
                            x + stoneSize + gap[0] + (size - stoneSize),
                            y,
                            COLOR_BLUE,
                            horizontalLineWidth,
                            doubleDottedLineGap,
                            horizontalLineDashArray
                        );
                    } else {
                        horizontalLine = svgLine(
                            x,
                            y,
                            x + stoneSize + gap[0] + (size - stoneSize),
                            y,
                            horizontalLineColor,
                            horizontalLineWidth,
                            horizontalLineDashArray
                        );
                    }
                    applyAnalysisLineStyle(
                        horizontalLine,
                        horizontalAnalysisStyle
                    );
                    if (deletedPreviewNodeIds.has(firstChildNode.model.id)) {
                        styleDeletedPreviewLine(horizontalLine, defaultLineWidth);
                    }
                    linesEl.appendChild(horizontalLine);
                }

                // Draw horizontal force adornment
                if (firstChildNode && isForceNode(node)) {
                    const {x, y} = positions[firstChildNode.model.id];
                    linesEl.appendChild(
                        svgEquilateralTriangle(
                            x - stoneSize - gap[0] / 2 - forceAdornmentSize / 4,
                            y,
                            forceAdornmentSize,
                            90,
                            lineColor,
                            // fill color
                            lineColor
                        )
                    );
                }

                // Draw horizontal block adornment
                if (
                    node.parent &&
                    isPreventMoveNode(node) &&
                    node.getIndex() === 0
                ) {
                    const {x, y} = positions[node.parent.model.id];
                    linesEl.appendChild(
                        svgSquare(
                            x +
                                stoneSize +
                                gap[0] / 2 +
                                preventMoveAdornmentSize / 4,
                            y,
                            preventMoveAdornmentSize,
                            lineColor,
                            // fill color
                            lineColor
                        )
                    );
                }

                // Draw vertical line
                if (
                    node.parent &&
                    node.parent.children.length > 0 &&
                    node.getIndex() !== 0 &&
                    // Skip vertical/diagonal lines for nodes on current path in pathOnly mode
                    !(
                        showCurrentPathOnly &&
                        positions[node.model.id]?.inCurrentPath
                    )
                ) {
                    const parentNode = node.parent;
                    const prevSiblingNode =
                        parentNode.children[node.getIndex() - 1];

                    const parentPos = positions[parentNode.model.id];
                    const siblingPos = positions[prevSiblingNode?.model?.id];

                    // Skip if positions are not available
                    if (!parentPos || !siblingPos) {
                        return;
                    }

                    const {x: px, y: py} = parentPos;
                    const {y: sy} = siblingPos;

                    // If the node is not in the right path and its following siblings are in the right path
                    // The vertical line should be in green
                    // Note: If following siblings are in the right path, the line color will be covered by the green line
                    let verticalLineColor = lineColor;
                    let verticalLineWidth = lineWidth;
                    let verticalLineDashArray = lineDashArray;
                    const followingSiblings = parentNode.children.filter(
                        (n: TNode) => n.getIndex() > node.getIndex()
                    );

                    const rightColors = followingSiblings.map((sn: TNode) =>
                        calcRightColor(sn)
                    );

                    // Same logic as above but for the active path
                    const inCurrentNodeArray = followingSiblings.map(
                        (sn: TNode) =>
                            inNodePath(sn, currentNode, activeBranchIndexes)
                    );

                    if (inCurrentNodeArray.includes(true)) {
                        verticalLineWidth = activeLineWidth;
                    }

                    if (!inRightPath(node) && followingSiblings.length > 0) {
                        verticalLineColor = rightColors.includes(rightLineColor)
                            ? rightLineColor
                            : lineColor;
                    }

                    let verticalLine;

                    // For vertical line and inclined line
                    if (
                        nodeIsAdded ||
                        newPathMatches?.changeType === SgfDiffType.Delete
                    ) {
                        verticalLineDashArray = DOTTED_LINE_DASH_ARRAY;
                    }

                    const verticalAnalysisStyle = getAnalysisLineStyle(
                        node,
                        verticalLineColor,
                        verticalLineDashArray
                    );
                    verticalLineColor = verticalAnalysisStyle.color;
                    verticalLineDashArray = nodeIsAdded
                        ? DOTTED_LINE_DASH_ARRAY
                        : verticalAnalysisStyle.dashArray;

                    if (
                        inUserCompressedPath(node, commentCompressedPaths) &&
                        offPath
                    ) {
                        verticalLine = svgDoubleLine(
                            px,
                            node.getIndex() >= 2 ? sy - size - gap[1] : py,
                            px,
                            y - size - gap[1],
                            inactiveLineColor,
                            verticalLineWidth,
                            doubleDottedLineGap,
                            verticalLineDashArray
                        );
                    } else if (
                        inUserCompressedPath(node, rightCompressedPaths) &&
                        offPath
                    ) {
                        verticalLine = svgDoubleLine(
                            px,
                            node.getIndex() >= 2 ? sy - size - gap[1] : py,
                            px,
                            y - size - gap[1],
                            COLOR_DARK_GREEN,
                            verticalLineWidth,
                            doubleDottedLineGap,
                            verticalLineDashArray
                        );
                    } else if (
                        inUserCompressedPath(node, wrongCompressedPaths) &&
                        offPath
                    ) {
                        verticalLine = svgDoubleLine(
                            px,
                            node.getIndex() >= 2 ? sy - size - gap[1] : py,
                            px,
                            y - size - gap[1],
                            COLOR_RED,
                            verticalLineWidth,
                            doubleDottedLineGap,
                            verticalLineDashArray
                        );
                    } else if (
                        inUserCompressedPath(node, questionCompressedPaths) &&
                        offPath
                    ) {
                        verticalLine = svgDoubleLine(
                            px,
                            node.getIndex() >= 2 ? sy - size - gap[1] : py,
                            px,
                            y - size - gap[1],
                            COLOR_BLUE,
                            verticalLineWidth,
                            doubleDottedLineGap,
                            verticalLineDashArray
                        );
                    } else {
                        verticalLine = svgLine(
                            px,
                            node.getIndex() >= 2 ? sy - size - gap[1] : py,
                            px,
                            y - size - gap[1],
                            verticalLineColor,
                            verticalLineWidth,
                            verticalLineDashArray
                        );
                    }
                    if (newPathMatches?.changeType === SgfDiffType.Delete) {
                        verticalLine.style.opacity = '0.5';
                    }
                    applyAnalysisLineStyle(verticalLine, verticalAnalysisStyle);
                    if (deletedPreviewNodeIds.has(node.model.id)) {
                        styleDeletedPreviewLine(verticalLine, defaultLineWidth);
                    }
                    linesEl.appendChild(verticalLine);

                    let inclinedline;
                    if (
                        inUserCompressedPath(node, commentCompressedPaths) &&
                        offPath
                    ) {
                        inclinedline = svgDoubleLine(
                            x - gap[0] - size,
                            y - gap[1] - size,
                            x,
                            y,
                            inactiveLineColor,
                            lineWidth,
                            doubleDottedLineGap,
                            verticalLineDashArray
                        );
                    } else if (
                        inUserCompressedPath(node, rightCompressedPaths) &&
                        offPath
                    ) {
                        inclinedline = svgDoubleLine(
                            x - gap[0] - size,
                            y - gap[1] - size,
                            x,
                            y,
                            COLOR_DARK_GREEN,
                            lineWidth,
                            doubleDottedLineGap,
                            verticalLineDashArray
                        );
                    } else if (
                        inUserCompressedPath(node, wrongCompressedPaths) &&
                        offPath
                    ) {
                        inclinedline = svgDoubleLine(
                            x - gap[0] - size,
                            y - gap[1] - size,
                            x,
                            y,
                            COLOR_RED,
                            lineWidth,
                            doubleDottedLineGap,
                            verticalLineDashArray
                        );
                    } else if (
                        inUserCompressedPath(node, questionCompressedPaths) &&
                        offPath
                    ) {
                        inclinedline = svgDoubleLine(
                            x - gap[0] - size,
                            y - gap[1] - size,
                            x,
                            y,
                            COLOR_BLUE,
                            lineWidth,
                            doubleDottedLineGap,
                            verticalLineDashArray
                        );
                    } else {
                        inclinedline = svgLine(
                            x - gap[0] - size,
                            y - gap[1] - size,
                            x,
                            y,
                            lineColor,
                            lineWidth,
                            verticalLineDashArray
                        );
                    }

                    if (newPathMatches?.changeType === SgfDiffType.Delete) {
                        inclinedline.style.opacity = '0.5';
                    }

                    applyAnalysisLineStyle(inclinedline, verticalAnalysisStyle);
                    if (deletedPreviewNodeIds.has(node.model.id)) {
                        styleDeletedPreviewLine(inclinedline, defaultLineWidth);
                    }

                    linesEl.appendChild(inclinedline);
                    // Draw vertical force adornment
                    if (parentNode && isForceNode(parentNode)) {
                        const angle =
                            Math.atan(
                                (gap[1] + stoneSize * 2) /
                                    (gap[0] + stoneSize * 2)
                            ) *
                            (180 / Math.PI);
                        linesEl.appendChild(
                            svgEquilateralTriangle(
                                x -
                                    stoneSize -
                                    gap[0] / 2 -
                                    forceAdornmentSize / 4,
                                y -
                                    stoneSize -
                                    stoneSize -
                                    forceAdornmentSize / 2 -
                                    lineWidth / 2 +
                                    // TODO: I didn't calculate the accurate offset base on the gap, just a workaround here
                                    38,
                                forceAdornmentSize,
                                90 + angle,
                                lineColor,
                                // fill color
                                lineColor
                            )
                        );
                    }

                    // Draw vertical block adornment
                    if (
                        node.parent &&
                        isPreventMoveNode(node) &&
                        node.getIndex() > 0
                    ) {
                        linesEl.appendChild(
                            svgSquare(
                                x -
                                    stoneSize -
                                    gap[0] / 2 -
                                    preventMoveAdornmentSize / 4,
                                y -
                                    stoneSize -
                                    stoneSize -
                                    forceAdornmentSize / 2 -
                                    lineWidth / 2 +
                                    // TODO: I didn't calculate the accurate offset base on the gap, just a workaround here
                                    28,
                                preventMoveAdornmentSize,
                                lineColor,
                                // fill color
                                lineColor
                            )
                        );
                    }
                }

                svgRef.current.appendChild(linesEl);
            }
            return true;
        });
    };

    const drawNode = (root: any) => {
        if (!root || !currentNode) return;
        calcPositions(root);
        calcPathOnlyPositions(root);
        drawLines();

        const positions = showCurrentPathOnly
            ? pathOnlyPositions
            : allPositions;

        root.walk((node: TNode) => {
            if (svgRef.current && positions[node.model.id]) {
                if (
                    showCurrentPathOnly &&
                    !positions[node.model.id].showInPathOnly
                ) {
                    return true;
                }

                const {x, y, treeDepth, treeHeight} = positions[node.model.id];
                const isDeletedPreviewNode = deletedPreviewNodeIds.has(
                    node.model.id
                );
                const el = svgEl('g', {
                    class: 'cursor-pointer',
                });
                if (isDeletedPreviewNode) {
                    el.style.opacity = '0.5';
                    const title = svgEl('title');
                    title.textContent = t('problem_draft.deleted_node_preview');
                    el.appendChild(title);
                }

                if (node.model.id === currentNode.model.id) {
                    const marker = svgEl('rect', {
                        id: 'sgf-tree-current-node',
                        x: x - size / 2,
                        y: y - size / 2,
                        width: size + squareHighlightExtent,
                        height: size + squareHighlightExtent,
                        fill: currentNodeHighlightColor,
                    });
                    svgRef.current.appendChild(marker);
                }

                const color = calcRightColor(node);
                if (isRightNode(node)) {
                    const c = svgCircle(
                        x,
                        y,
                        color,
                        size / 2 + circleHighlightExtent
                    );
                    if (isDeletedPreviewNode) c.style.opacity = '0.5';
                    svgRef.current.appendChild(c);
                }

                const {setupProps, moveProps} = node.model;

                if (node.model.rootProps.length > 0) {
                    el.appendChild(
                        svgIcon(
                            x,
                            y,
                            icon(faHouse).html[0],
                            (size / 2) * SCALE * 0.95,
                            'black'
                        )
                    );
                } else if (node.model.gameInfoProps.length > 0) {
                    el.appendChild(
                        svgIcon(
                            x,
                            y,
                            icon(faCircleInfo).html[0],
                            (size / 2) * SCALE * 0.8,
                            'black'
                        )
                    );
                } else if (moveProps.length > 0) {
                    const moveToken = node.model.moveProps[0].token;
                    const color = moveToken === 'B' ? 'black' : 'white';
                    el.appendChild(svgStone(x, y, color, stoneSize));
                } else if (setupProps.length > 0) {
                    el.appendChild(svgStone(x, y, 'black', stoneSize));
                }

                el.onclick = () => {
                    handleNodeChange(node);
                };

                el.oncontextmenu = (e: MouseEvent) => {
                    e.preventDefault();
                    e.stopPropagation();

                    if (onNodeRightClick && !isDeletedPreviewNode) {
                        onNodeRightClick(node, e);
                    }
                };

                svgRef.current.appendChild(el);

                if (findProps(node, 'A').length > 0) {
                    const analysis = svgEl('polygon', {
                        points: `${x + 20},${y + size / 2} ${x + size / 2},${
                            y + size / 2
                        } ${x + size / 2},${y + 20}`,
                        fill: 'rgb(136, 170, 60)',
                    });
                    svgRef.current.appendChild(analysis);
                }

                const parent = node.parent;
                if (showAnalysis && parent) {
                    const analysisProp = findProp(parent, 'A');
                    if (analysisProp) {
                        const analysis: Analysis = JSON.parse(
                            Base64.decode(analysisProp.value)
                        );
                        const rootInfo = analysis.rootInfo;
                        const moveInfo = analysis.moveInfos.find(m => {
                            const pos = a1ToPos(m.move);
                            if (node.model?.moveProps[0]?.value) {
                                return (
                                    node.model?.moveProps[0]?.value ===
                                    `${SGF_LETTERS[pos.x]}${SGF_LETTERS[pos.y]}`
                                );
                            }
                            return false;
                        });
                        if (rootInfo && moveInfo) {
                            const color = calcAnalysisPointColor(
                                rootInfo,
                                moveInfo
                            );
                            el.appendChild(svgCircle(x, y, color));
                            el.appendChild(
                                svgLabel(
                                    x,
                                    y,
                                    'white',
                                    calcScoreDiffText(rootInfo, moveInfo, 1),
                                    false
                                )
                            );
                        }
                    }
                }

                // Show score on nodes using SC (Score) custom property
                if (showTotalLoss && node.model.rootProps.length === 0) {
                    const scoreValue = getCustomPropValue(node, 'SC');
                    if (scoreValue) {
                        const score = parseFloat(scoreValue);
                        if (!isNaN(score)) {
                            // score is from player perspective:
                            // positive = good (green), zero = neutral (gray), negative = bad (red)
                            const color =
                                score > 0
                                    ? COLOR_DARK_GREEN
                                    : score < 0
                                      ? COLOR_RED
                                      : COLOR_GRAY;
                            el.appendChild(
                                svgCircle(x, y, color, stoneSize - 8)
                            );
                            el.appendChild(
                                svgLabel(
                                    x,
                                    y,
                                    'white',
                                    (score > 0 ? '+' : '') + score.toFixed(1),
                                    false
                                )
                            );
                        }
                    }
                }

                if (pathCounts && node.model.rootProps.length === 0) {
                    const playCount =
                        pathCounts[getCompressedPathFromNode(node)] ?? 0;

                    if (playCount > 0) {
                        const countLabel = svgEl('text', {
                            x: x + stoneSize * 0.35,
                            y: y + stoneSize * 0.95,
                            'font-size': 40,
                            'font-weight': 700,
                            'text-anchor': 'start',
                            'dominant-baseline': 'middle',
                            'font-family': 'Helvetica, Arial, sans-serif',
                            fill: 'var(--ui-text)',
                            stroke: 'var(--ui-surface-active)',
                            'stroke-width': 10,
                            'paint-order': 'stroke',
                        });
                        countLabel.appendChild(
                            document.createTextNode(String(playCount))
                        );
                        svgRef.current.appendChild(countLabel);
                    }
                }

                const lazyPath = getNodeA1Path(node);
                const lazyState = lazyLoadStates?.[lazyPath];
                if (
                    lazyState &&
                    lazyState.childrenCount > 0 &&
                    !lazyState.childrenLoaded
                ) {
                    const lazyMarker = svgEl('g', {
                        class: 'cursor-pointer',
                    });
                    const markerX = x + size / 2 + 28;
                    const markerY = y;

                    if (lazyState.isLoading) {
                        const spinner = svgEl('circle', {
                            cx: markerX,
                            cy: markerY,
                            r: 16,
                            fill: 'none',
                            stroke: COLOR_BLUE,
                            'stroke-width': 6,
                            'stroke-dasharray': '28 18',
                        });
                        const animation = svgEl('animateTransform', {
                            attributeName: 'transform',
                            attributeType: 'XML',
                            type: 'rotate',
                            from: `0 ${markerX} ${markerY}`,
                            to: `360 ${markerX} ${markerY}`,
                            dur: '0.8s',
                            repeatCount: 'indefinite',
                        });
                        spinner.appendChild(animation);
                        lazyMarker.appendChild(spinner);
                    } else {
                        lazyMarker.appendChild(
                            svgCircle(
                                markerX,
                                markerY,
                                'var(--ui-surface-active)',
                                18,
                                COLOR_BLUE,
                                5
                            )
                        );
                        lazyMarker.appendChild(
                            svgEl('line', {
                                x1: markerX - 9,
                                y1: markerY,
                                x2: markerX + 9,
                                y2: markerY,
                                stroke: COLOR_BLUE,
                                'stroke-width': 6,
                                'stroke-linecap': 'round',
                            })
                        );
                        lazyMarker.appendChild(
                            svgEl('line', {
                                x1: markerX,
                                y1: markerY - 9,
                                x2: markerX,
                                y2: markerY + 9,
                                stroke: COLOR_BLUE,
                                'stroke-width': 6,
                                'stroke-linecap': 'round',
                            })
                        );
                    }

                    lazyMarker.onclick = (event: MouseEvent) => {
                        event.preventDefault();
                        event.stopPropagation();
                        onLoadLazyNode?.(node);
                    };
                    svgRef.current.appendChild(lazyMarker);
                }

                // Show blue ring on nodes with positive endness using ED custom property
                const endnessValue = getCustomPropValue(node, 'ED');
                if (endnessValue) {
                    const endness = parseFloat(endnessValue);
                    if (!isNaN(endness) && endness > 0) {
                        el.appendChild(svgRing(x, y, COLOR_BLUE, size, 12));
                    }
                }

                if (isUserCompressedPathNode(node, commentCompressedPaths)) {
                    el.appendChild(
                        svgIcon(
                            x + size / 2 + 10,
                            y + size / 2 + 20,
                            icon(faComment).html[0],
                            (size / 2) * SCALE * 0.5,
                            isLatestUserCommentNode(
                                node,
                                commentCompressedPaths
                            )
                                ? theme === 'dark'
                                    ? 'white'
                                    : 'black'
                                : inactiveLineColor
                        )
                    );
                } else if (
                    isUserCompressedPathNode(node, rightCompressedPaths)
                ) {
                    el.appendChild(
                        svgIcon(
                            x + size / 2 + 5,
                            y + size / 2 + 25,
                            icon(faComment).html[0],
                            (size / 2) * SCALE * 0.5,
                            COLOR_DARK_GREEN
                        )
                    );
                } else if (
                    isUserCompressedPathNode(node, wrongCompressedPaths)
                ) {
                    el.appendChild(
                        svgIcon(
                            x + size / 2 + 5,
                            y + size / 2 + 25,
                            icon(faComment).html[0],
                            (size / 2) * SCALE * 0.5,
                            COLOR_RED
                        )
                    );
                } else if (
                    isUserCompressedPathNode(node, questionCompressedPaths)
                ) {
                    el.appendChild(
                        svgIcon(
                            x + size / 2 + 10,
                            y + size / 2 + 10,
                            icon(faComment).html[0],
                            (size / 2) * SCALE * 0.5,
                            COLOR_BLUE
                        )
                    );
                }

                const nodeCompressedPath = getCompressedPathFromNode(node);
                const exactPathMatches = changedCompressedPaths.find(n => {
                    return n.fullPath === nodeCompressedPath;
                });

                if (
                    exactPathMatches?.changeType === SgfDiffType.Choice ||
                    exactPathMatches?.changeType === SgfDiffType.Comment ||
                    exactPathMatches?.changeType === SgfDiffType.Force ||
                    exactPathMatches?.changeType === SgfDiffType.Markup ||
                    exactPathMatches?.changeType === SgfDiffType.NotThis ||
                    exactPathMatches?.changeType === SgfDiffType.Setup ||
                    exactPathMatches?.changeType === SgfDiffType.Result
                ) {
                    const size = stoneSize;
                    el.appendChild(
                        svgCircle(
                            x - size + 5,
                            y + size - 5,
                            COLOR_BLUE,
                            size * 0.5,
                            COLOR_BLUE
                        )
                    );
                }

                const newPathMatches = changedCompressedPaths.find(n => {
                    return (
                        n.fullPath.startsWith(nodeCompressedPath) &&
                        !n.basePath.startsWith(nodeCompressedPath) &&
                        !isEmpty(nodeCompressedPath)
                    );
                });

                if (
                    newPathMatches?.changeType === SgfDiffType.Add ||
                    (showEditPathMarkers &&
                        editedNodeIds?.has(node.model.id) &&
                        baselineNodeIds?.has(node.model.id) === false)
                ) {
                    const size = stoneSize;
                    el.appendChild(
                        svgIcon(
                            x - size / 2 - 30,
                            y + size / 2 + 10,
                            icon(faCirclePlus).html[0],
                            size * 0.15,
                            COLOR_DARK_GREEN
                        )
                    );
                }

                if (
                    newPathMatches?.changeType === SgfDiffType.Delete ||
                    isDeletedPreviewNode
                ) {
                    const size = stoneSize;
                    if (
                        newPathMatches?.changeType === SgfDiffType.Delete &&
                        !isDeletedPreviewNode
                    ) {
                        el.style.opacity = '0.7';
                    }
                    el.appendChild(
                        svgIcon(
                            x - size / 2 - 30,
                            y + size / 2 + 10,
                            icon(faCircleMinus).html[0],
                            size * 0.15,
                            COLOR_RED
                        )
                    );
                }

                if (isTargetNode(node)) {
                    el.appendChild(
                        svgIcon(
                            x + size / 4,
                            y + size / 4,
                            icon(faBullseye).html[0],
                            (size / 2) * SCALE * 0.7,
                            COLOR_LIGHT_RED
                        )
                    );
                }

                if (isSgfCommentNode(node)) {
                    if (moveProps.length > 0) {
                        const moveToken = node.model.moveProps[0].token;
                        const color = moveToken === 'B' ? 'white' : 'black';
                        const c = svgCircle(x, y, color, size / 7);
                        svgRef.current.appendChild(c);
                    }
                }

                // Add some bottom/right padding for the comment icon
                const PADDING = lazyLoadStates ? 80 : 40;
                const calculatedHeight =
                    Math.max(
                        treeLeafCount * size +
                            size +
                            treeHeight * gap[1] +
                            PADDING,
                        minHeight
                    ) * SCALE;
                const calculatedWidth =
                    Math.max(
                        treeDepth * size + size + treeDepth * gap[0] + PADDING,
                        minWidth
                    ) * SCALE;
                setHeight(calculatedHeight);
                setWidth(calculatedWidth);
            }
            return true;
        });
    };

    const render = () => {
        if (!currentNode) return;
        const root = getRoot(currentNode);
        const removeAllChildNodes = (parent: any) => {
            while (parent.firstChild) {
                parent.removeChild(parent.firstChild);
            }
        };
        if (svgRef.current) {
            removeAllChildNodes(svgRef.current);
            const bg = svgEl('rect', {
                height: '100%',
                width: '100%',
                class: 'gp-svg-tree',
                fill: 'var(--ui-surface-active)',
            });
            svgRef.current.appendChild(bg);
            drawNode(root);
            if (autoScrollToCurrentNode) scrollToCurrentNode();
        }
    };

    useEffect(() => {
        setRoot(deletedEditPreviewRoot ?? originalRoot);
        setCurrentNode(
            deletedEditPreviewRoot
                ? deletedEditPreviewRoot.first(
                      n => n.model.id === originalCurrentNode?.model.id
                  ) ?? deletedEditPreviewRoot
                : originalCurrentNode
        );
        setUpdate(originalUpdate);
    }, [
        originalCurrentNode,
        originalRoot,
        originalUpdate,
        deletedEditPreviewRoot,
    ]);

    useEffect(() => {
        if (!originalRoot || !showDeletedPaths) return;

        changedCompressedPaths.forEach(path => {
            const compressedPath = path.fullPath;
            const existedNode = findNodeFromCompressedPath(
                originalRoot,
                compressedPath
            );
            if (existedNode) return;
            let turn = getFirstToMoveColorFromRoot(originalRoot);
            const compressedPathArray = compressedPath.match(/.{1,2}/g) || [];
            let newNode = originalRoot;
            compressedPathArray.forEach(path => {
                const i = SGF_LETTERS.indexOf(path[0].toLowerCase());
                const j = SGF_LETTERS.indexOf(path[1].toLowerCase());
                const {mat} = calcMatAndMarkup(newNode);
                const n = addMoveToCurrentNode(newNode, mat, i, j, turn);
                if (!n) {
                    console.log('failed path', compressedPath, path);
                    return;
                }
                turn = turn === Ki.Black ? Ki.White : Ki.Black;
                const root = getRoot(n);
                setRoot(root);
                newNode = n;
            });
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [changedCompressedPaths]);

    useEffect(() => {
        if (!baseSgf) return;
        const problemTree = new Sgf(baseSgf);
        const root = problemTree.root;
        setBaseRootNode(root);
    }, [baseSgf]);

    useEffect(() => {
        render();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        currentNode,
        update,
        activeBranchIndexes,
        showTotalLoss,
        showAnalysis,
        showCurrentPathOnly,
        pathCounts,
        lazyLoadStates,
        onLoadLazyNode,
        minWidthOption,
        minHeightOption,
        baselineNodeIds,
        editedNodeIds,
        deletedPreviewNodeIds,
    ]);

    useDeepCompareEffect(() => {
        render();
    }, [
        commentCompressedPaths,
        rightCompressedPaths,
        wrongCompressedPaths,
        questionCompressedPaths,
        changedCompressedPaths,
    ]);

    const mouseMoveHandler = (e: MouseEvent) => {
        const container = containerRef.current;
        if (!container) return;

        // How far the mouse has been moved
        const dx = e.clientX - pos.x;
        const dy = e.clientY - pos.y;

        // Scroll the panel
        container.scrollTop = pos.top - dy;
        container.scrollLeft = pos.left - dx;
    };

    const mouseUpHandler = () => {
        const container = containerRef.current;
        if (!container) return;

        container.style.cursor = 'grab';
        container.style.removeProperty('user-select');

        document.removeEventListener('mousemove', mouseMoveHandler);
        document.removeEventListener('mouseup', mouseUpHandler);
    };

    const mouseDownHandler = (
        e: React.MouseEvent<HTMLDivElement, MouseEvent>
    ) => {
        const container = containerRef.current;
        if (!container) return;

        container.style.cursor = 'grabbing';
        container.style.userSelect = 'none';

        pos = {
            left: container.scrollLeft,
            top: container.scrollTop,
            // Get the current mouse position
            x: e.clientX,
            y: e.clientY,
        };

        document.addEventListener('mousemove', mouseMoveHandler);
        document.addEventListener('mouseup', mouseUpHandler);
    };

    useEffect(() => {
        if (!currentNode) return;
        if (currentNode.getIndex() <= 0) return;
        setActiveBranchIndexes(prev => {
            const newIndexes = {...prev};
            if (currentNode.getIndex() > 0 && currentNode.parent) {
                newIndexes[currentNode.parent.model.id] =
                    currentNode.getIndex();
            }
            return newIndexes;
        });
    }, [currentNode]);

    useEffect(() => {
        dispatch(
            boardSlice.actions.setActiveBranchIndexes(activeBranchIndexes)
        );
    }, [activeBranchIndexes, dispatch]);

    const [showJumpButton, setShowJumpButton] = useState(false);

    useEffect(() => {
        if (!containerRef.current || !currentNode) return;

        let hideTimeoutId: ReturnType<typeof setTimeout> | null = null;

        const checkVisibility = () => {
            const container = containerRef.current;
            const el =
                container?.querySelector<SVGGraphicsElement>(
                    CURRENT_NODE_SELECTOR
                );
            if (!el || !container) return;

            const cRect = container.getBoundingClientRect();
            const eRect = el.getBoundingClientRect();
            const isVisible =
                eRect.top >= cRect.top &&
                eRect.bottom <= cRect.bottom &&
                eRect.left >= cRect.left &&
                eRect.right <= cRect.right;

            if (isVisible) {
                // Node is visible, cancel any pending show and hide the button
                if (hideTimeoutId) {
                    clearTimeout(hideTimeoutId);
                    hideTimeoutId = null;
                }
                setShowJumpButton(false);
            } else {
                // Node is not visible, schedule button show after 1s
                if (!hideTimeoutId) {
                    hideTimeoutId = setTimeout(() => {
                        setShowJumpButton(true);
                        hideTimeoutId = null;
                    }, 1000);
                }
            }
        };

        const timeoutId = setTimeout(checkVisibility, 100);
        const container = containerRef.current;
        container?.addEventListener('scroll', checkVisibility);

        return () => {
            clearTimeout(timeoutId);
            if (hideTimeoutId) clearTimeout(hideTimeoutId);
            container?.removeEventListener('scroll', checkVisibility);
        };
    }, [currentNode, update]);

    const handleJumpToCurrentNode = () => {
        scrollToCurrentNode();
    };

    return (
        <div className="relative">
            <div
                className="overflow-x-auto flex-1"
                style={{
                    maxWidth: `${maxWidth}px`,
                    maxHeight: `${maxHeight}px`,
                }}
                ref={containerRef}
                onMouseDown={mouseDownHandler}>
                <svg
                    width={width}
                    height={height}
                    ref={svgRef}
                    viewBox={`0 0 ${width / SCALE} ${height / SCALE}`}
                    preserveAspectRatio="none"
                />
            </div>
            <div
                onClick={handleJumpToCurrentNode}
                className={`absolute bg-body bottom-3 right-3 rounded-full transition-all duration-300 hover:scale-110 flex items-center justify-center cursor-pointer ${
                    showJumpButton
                        ? 'opacity-100 pointer-events-auto'
                        : 'opacity-0 pointer-events-none'
                }`}
                title={t('problem.jump_to_current_node')}>
                <FontAwesomeIcon
                    icon={faBullseye}
                    className="text-red-500"
                    size="lg"
                />
            </div>
        </div>
    );
};
